import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { differenceInDays, format, isAfter, parse } from 'date-fns';

import type { AdditionalPurchase, LicensedPeriodDPH, NotificationFrequency } from '@/types';
import { logger } from '@/utils/logger';

import { findNotificationFrequency } from '../utils/dph.utils';
import { UsageDoughnutChart } from './UsageDoughnutChart';

interface LicensedPeriodCardProps {
  data: {
    licensedPeriodDPH: LicensedPeriodDPH;
    tenantDPHAdditionalPurchases: AdditionalPurchase[];
  };
  setType: (type: string) => void;
  setEditModal: (open: boolean) => void;
  notificationFrequency: NotificationFrequency[];
}

/**
 * Card displaying licensed period DPH summary
 * Shows license dates, usage doughnut chart, additional purchases,
 * and notification settings
 */
export const LicensedPeriodCard = ({
  data,
  setType,
  setEditModal,
  notificationFrequency,
}: LicensedPeriodCardProps) => {
  const { licensedPeriodDPH, tenantDPHAdditionalPurchases } = data;

  // Check if data exists before destructuring
  if (!licensedPeriodDPH) {
    return null;
  }

  const {
    startDate,
    endDate,
    dataProcHours,
    usedDataProcHours,
    usedDataProcHoursPercentage,
    availableDataProcHours,
    availableDataProcHoursPercentage,
    threshold,
    thresholdPercentage,
    thresholdValueHours,
    thresholdRemainingHours,
    notificationEmails,
    notificationFrequencyId,
  } = licensedPeriodDPH;

  // Parse and format dates safely
  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;
    try {
      // Try parsing with expected format first
      const parsed = parse(dateStr, 'MMM dd, yyyy', new Date());
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
      // Fallback to ISO format
      const isoParsed = new Date(dateStr);
      if (!isNaN(isoParsed.getTime())) {
        return isoParsed;
      }
    } catch (_error) {
      logger.warn('Invalid date format:', dateStr);
    }
    return null;
  };

  const getDphPeriod = () => {
    try {
      const start = parseDate(startDate);
      const end = parseDate(endDate);

      if (!start || !end) {
        return 'Invalid date format';
      }

      return `(${format(start, 'MMM dd, yyyy')} - ${format(end, 'MMM dd, yyyy')})`;
    } catch (error) {
      logger.error('Error formatting date period:', error);
      return '';
    }
  };

  const getLicenseStatus = () => {
    const end = parseDate(endDate);
    if (!end) return null;

    const now = new Date();
    const daysRemaining = differenceInDays(end, now);

    if (isAfter(now, end)) {
      return {
        type: 'error' as const,
        message: 'License Expired',
      };
    } else if (daysRemaining <= 10) {
      return {
        type: 'warning' as const,
        message: `License Expires in ${daysRemaining} days`,
      };
    }
    return null;
  };

  const licenseStatus = getLicenseStatus();

  return (
    <Card
      onClick={() => setType('LicensedPeriod')}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <strong>Licensed Period Summary</strong>
        </Typography>

        {/* License Period Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            <strong>🗓️ License Period:</strong> {getDphPeriod()}
            {licenseStatus && (
              <Chip
                label={licenseStatus.message}
                color={licenseStatus.type}
                size="small"
                icon={<span>⚠️</span>}
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>

        {/* Usage Chart */}
        <Box sx={{ width: 300, mx: 'auto', my: 3 }}>
          <UsageDoughnutChart
            dataProcHours={dataProcHours}
            usedDataProcHours={usedDataProcHours}
            usedDataProcHoursPercentage={usedDataProcHoursPercentage}
            availableDataProcHours={availableDataProcHours}
            availableDataProcHoursPercentage={availableDataProcHoursPercentage}
            threshold={threshold}
            thresholdPercentage={thresholdPercentage}
            thresholdValueHours={thresholdValueHours}
            thresholdRemainingHours={thresholdRemainingHours}
          />
        </Box>

        {/* Additional Purchases - Fixed height section to match Monthly card */}
        <Box
          sx={{
            minHeight: 68,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {tenantDPHAdditionalPurchases && tenantDPHAdditionalPurchases.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
              {tenantDPHAdditionalPurchases.map((purchase) => (
                <Typography
                  key={`${purchase.purchasedDate}-${purchase.dataProcHours}`}
                  variant="body2"
                  sx={{
                    color: 'info.main',
                    textAlign: 'center',
                  }}
                >
                  {purchase.dataProcHours} hours Added on {purchase.purchasedDate}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>

        {/* Notification Settings */}
        <Card
          variant="outlined"
          sx={{
            p: 2,
            borderColor: 'grey.400',
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ borderBottom: 'none' }}>
            Notifications
          </Typography>

          <Typography variant="body2" component="div" sx={{ mb: 2 }}>
            Threshold set at <strong>{thresholdPercentage}%</strong> of{' '}
            <strong>{dataProcHours}</strong> hours at{' '}
            <Chip
              label={findNotificationFrequency(notificationFrequency, notificationFrequencyId)}
              size="small"
              sx={{ ml: 0.5 }}
            />
          </Typography>

          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Emails
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {notificationEmails &&
              notificationEmails.map((email) => (
                <Chip key={email} label={email} size="small" variant="outlined" />
              ))}
          </Box>

          <Button variant="contained" onClick={() => setEditModal(true)} size="small">
            Edit
          </Button>
        </Card>
      </CardContent>
    </Card>
  );
};
